import clsx from 'clsx';
import { useContext, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { writeUserToStorage } from '@vestico/model';

import { FormValidationContext } from '../CommonAppContext';
import { BraSelect, BraSelectConfiguration } from '../components/BraSelect';
import { useIsMounted } from '../hooks/mounted';
import { getBraConfiguration } from '../model/configurations';
import { StepProps } from '../model/step';
import { UserContext } from '../model/userContext';
import StepContainer from './StepContainer';
import style from './braform.module.css';

export const BraForm = (props: StepProps) => {
  const { user } = useContext(UserContext);
  const formValidationContext = useContext(FormValidationContext);
  const mounted = useIsMounted();
  const { i18n } = useTranslation();

  const [submitting, setSubmitting] = useState(false);
  useMemo(() => {
    const isSubmittingFromExternally = props.submitting;
    if (isSubmittingFromExternally) {
      setSubmitting(isSubmittingFromExternally);
    }
    return isSubmittingFromExternally;
  }, [props.submitting]);

  const [braConfiguration, setBraConfiguration] = useState(getBraConfiguration(i18n.language));

  const braError = useMemo(() => {
    return !!(mounted.current && (!braConfiguration.value.band || !braConfiguration.value.cup));
  }, [braConfiguration, submitting, mounted]);

  const formValid = useMemo(() => !braError, [braError]);

  braConfiguration.didUpdateConfig = (newConfig: BraSelectConfiguration) => {
    setBraConfiguration(newConfig);
  };

  useMemo(() => {
    if (user === null) {
      return;
    }

    braConfiguration.value.band = user.braSizeBand;
    braConfiguration.value.cup = user.braSizeCup;
  }, [user]);

  function updateUserProperties() {
    if (user === null) {
      console.error('no user fetched');
      return;
    }

    user.braSizeBand = braConfiguration.value.band;
    user.braSizeCup = braConfiguration.value.cup;
    user.braSizeSourceId = braConfiguration.options[braConfiguration.selectedOptionIndex].id;
  }

  useLayoutEffect(() => {
    if (!submitting) {
      return;
    }

    formValidationContext.updateStep(props.path, formValid);
    braConfiguration.showErrorIfNeeded = true;

    if (!formValid) {
      setSubmitting(false);
      return;
    }

    if (!user) {
      console.error('missing user');
      return;
    }

    // store data
    updateUserProperties();
    writeUserToStorage(user);

    setSubmitting(false);
    braConfiguration.showErrorIfNeeded = false;
  }, [formValid, submitting]);

  return (
    <StepContainer title={props.title}>
      <form
        className={clsx(style.formLayout)}
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitting(true);
        }}
      >
        <BraSelect configuration={braConfiguration} />
      </form>
    </StepContainer>
  );
};

export default BraForm;
